import { OfflineDatabase } from "../database";
import { CachedPartner, STORES } from "../schema";
import type { Partner } from "@/app/api/types/partner";

export class PartnerStore {
  constructor(private db: OfflineDatabase) {}

  // Get partner by ID (works for both temp and real IDs)
  async get(partnerId: string): Promise<CachedPartner | null> {
    return this.db.get<CachedPartner>(STORES.PARTNERS, partnerId);
  }

  // Get partner object by ID
  async getPartner(partnerId: string): Promise<Partner | null> {
    const cached = await this.get(partnerId);
    return cached?.partner ?? null;
  }

  // Get all offline (unsynced) partners
  async getOfflinePartners(): Promise<CachedPartner[]> {
    return this.db.queryByIndex<CachedPartner>(
      STORES.PARTNERS,
      "isOffline",
      1, // IndexedDB stores booleans as 0/1
    );
  }

  // Get all cached partners
  async getAll(): Promise<CachedPartner[]> {
    return this.db.getAll<CachedPartner>(STORES.PARTNERS);
  }

  // Cache a new offline partner
  async addOffline(partner: Partner): Promise<void> {
    const entry: CachedPartner = {
      id: partner.id as string,
      partner,
      isOffline: true,
      createdAt: Date.now(),
    };
    await this.db.set(STORES.PARTNERS, entry);
  }

  // Mark partner as synced (update with real ID)
  async markSynced(tempId: string, realPartner: Partner): Promise<void> {
    // Remove temp entry
    await this.db.delete(STORES.PARTNERS, tempId);

    // Add synced entry with real ID
    const entry: CachedPartner = {
      id: realPartner.id as string,
      partner: realPartner,
      isOffline: false,
      createdAt: Date.now(),
      syncedAt: Date.now(),
    };
    await this.db.set(STORES.PARTNERS, entry);
  }

  // Update partner in cache (for local edits)
  async update(
    partnerId: string,
    updates: Partial<Partner>,
  ): Promise<Partner | null> {
    const entry = await this.get(partnerId);
    if (!entry) return null;

    entry.partner = {
      ...entry.partner,
      ...updates,
    };

    await this.db.set(STORES.PARTNERS, entry);
    return entry.partner;
  }

  // Delete partner from cache
  async delete(partnerId: string): Promise<void> {
    await this.db.delete(STORES.PARTNERS, partnerId);
  }

  // Clear all cached partners (for logout/reset)
  async clear(): Promise<void> {
    const all = await this.getAll();
    await Promise.all(all.map((p) => this.db.delete(STORES.PARTNERS, p.id)));
  }

  // Check if a partner ID is an offline temp ID
  isOfflineId(partnerId: string): boolean {
    return partnerId.startsWith("temp-partner-");
  }
}
